// Resuelve título+código del Questionnaire referenciado por cada
// QuestionnaireResponse — usado por NotasTab.tsx/HistoriaTab.tsx para poder
// mostrar un nombre legible y linkear a la nota en vez de imprimir la
// referencia FHIR cruda ("Questionnaire/2b573d54-..."). Bug real reportado
// en vivo 2026-09-03: esas dos listas mostraban la referencia como texto
// plano, sin ninguna forma de abrir/leer/editar la nota ya creada.
import { useMedplum } from '@medplum/react-hooks';
import type { Questionnaire, QuestionnaireResponse } from '@medplum/fhirtypes';
import { useEffect, useState } from 'react';

const SYSTEM_CODIGO_FORMULARIO = 'http://logiacare.ec/fhir/formularios-msp';

/**
 * Códigos que NO pasan por el motor genérico de un solo paso
 * (MotorFormulario.tsx, vía la ruta catch-all "formulario/:codigo") — 024 y
 * FEMO son wizards de varios pasos con firma manuscrita entre medio
 * (MotorFormularioConsentimiento.tsx/FormularioFemoPage.tsx, ver
 * routes.tsx). Retomar un wizard abandonado a medio camino (antes de la
 * firma) no está soportado en esta pasada — se listan sin link, mismo
 * comportamiento que antes de este fix, en vez de linkear a una ruta que no
 * sabe reanudar el wizard.
 */
const CODIGOS_SIN_VISTA_GENERICA = new Set([
  '024',
  'FEMO',
  // Los 6 consentimientos nuevos (lib/consentimientos.ts) son el mismo tipo
  // de wizard multi-paso con firma que 024 — mismo criterio, no reanudable
  // desde el motor genérico.
  'consent-quirurgico',
  'consent-anestesia',
  'consent-transfusion',
  'consent-telesalud-canal',
  'consent-telesalud-lopdp',
  'consent-voluntad-anticipada',
]);

export type InfoFormulario = { codigo: string; titulo: string; editable: boolean };

/** `Map` con clave = referencia completa ("Questionnaire/<id>", tal cual viene en `QuestionnaireResponse.questionnaire`). Ausente = no se pudo resolver (Questionnaire borrado, o la búsqueda todavía no volvió). */
export function useTitulosFormulario(respuestas: readonly QuestionnaireResponse[]): Map<string, InfoFormulario> {
  const medplum = useMedplum();
  const [mapa, setMapa] = useState<Map<string, InfoFormulario>>(new Map());

  const ids = Array.from(new Set(respuestas.map((r) => r.questionnaire).filter((ref): ref is string => !!ref).map((ref) => ref.replace('Questionnaire/', ''))));
  // Clave estable derivada de los ids — `respuestas` es un array nuevo en
  // cada render (viene de useSearchResources/traerHistoriaClinica), así que
  // depender de él directamente reharía el fetch en cada render sin que los
  // datos hayan cambiado.
  const clave = ids.slice().sort().join(',');

  useEffect(() => {
    if (ids.length === 0) {
      setMapa(new Map());
      return;
    }
    let cancelado = false;
    medplum
      .search('Questionnaire', { _id: ids.join(','), _count: ids.length })
      .then((bundle) => {
        if (cancelado) return;
        const nuevo = new Map<string, InfoFormulario>();
        for (const entry of bundle.entry ?? []) {
          const q = entry.resource as Questionnaire | undefined;
          if (!q?.id) continue;
          const codigo = q.identifier?.find((i) => i.system === SYSTEM_CODIGO_FORMULARIO)?.value;
          if (!codigo) continue;
          nuevo.set(`Questionnaire/${q.id}`, { codigo, titulo: q.title ?? codigo, editable: !CODIGOS_SIN_VISTA_GENERICA.has(codigo) });
        }
        setMapa(nuevo);
      })
      .catch(() => {
        if (!cancelado) setMapa(new Map());
      });
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `clave` ya resume todo lo que puede cambiar en `ids`
  }, [medplum, clave]);

  return mapa;
}
